import { useCallback } from 'react';
import { useApiService, ApiCallOptions } from './apiService';

// Types
export interface Tenant {
  id: string;
  name: string;
  status: 'Active' | 'Inactive';
  createdDate: string;
  features: string[];
}

export interface User {
  id: string;
  email: string;
  role: string;
  tenantId: string;
}

export interface TenantFeature {
  id: string;
  name: string;
  description: string;
  category: string;
}

export interface TenantFeatureMappingRequest {
  tenantId: string;
  features: string[];
}

// Mock Data - fallback when API is not available
const MOCK_TENANTS: Tenant[] = [
  {
    id: "1",
    name: "Acme Aviation",
    status: "Active",
    createdDate: "2024-01-15",
    features: ["manage-assemblies", "manage-airframes", "assets-bulk-template-upload", "document-search-view-download"],
  },
  {
    id: "2",
    name: "SkyHigh Airlines", 
    status: "Active",
    createdDate: "2024-02-20",
    features: ["manage-assemblies", "manage-airframes", "assets-bulk-template-upload", "document-search-view-download", "upload-documents"],
  },
  {
    id: "3",
    name: "FlightPath Corp",
    status: "Inactive",
    createdDate: "2024-03-10",
    features: ["document-search-view-download"],
  },
];

const MOCK_USERS: User[] = [
   { id: "admin-user", email: "anuja.kulkarni@centricconsulting.com", role: "Admin", tenantId: "1" },
  { id: "admin-user", email: "admin@example.com", role: "Admin", tenantId: "1" },
  { id: "user-2", email: "test.user@gmail.com", role: "Admin", tenantId: "1" },
  { id: "user-3", email: "jane.smith@example.com", role: "Admin", tenantId: "1" },
  { id: "user-4", email: "mike.wilson@example.com", role: "Admin", tenantId: "1" },
  { id: "user-5", email: "john.doe@example.com", role: "Admin", tenantId: "1" },
  { id: "user-6", email: "sarah.johnson@example.com", role: "Admin", tenantId: "2" },
  { id: "user-7", email: "robert.brown@example.com", role: "Admin", tenantId: "2" },
  { id: "user-8", email: "emily.davis@example.com", role: "Admin", tenantId: "2" },
  { id: "user-9", email: "michael.wilson@example.com", role: "Viewer", tenantId: "3" },
  { id: "user-10", email: "lisa.anderson@example.com", role: "Manager", tenantId: "3" },
];

const MOCK_FEATURES: TenantFeature[] = [
  {
    id: "manage-assemblies",
    name: "Manage Assemblies",
    description: "Manage aircraft assemblies and components",
    category: "Assets",
  },
  {
    id: "manage-airframes",
    name: "Manage Airframes",
    description: "Manage aircraft airframes and structures",
    category: "Assets",
  },
  {
    id: "assets-bulk-template-upload",
    name: "Bulk Template Upload",
    description: "Upload JSON templates and download Excel import sheets",
    category: "Assets",
  },
  {
    id: "document-search-view-download",
    name: "Document Search, View & Download",
    description: "Search, view and download aviation documents",
    category: "Documents",
  },
  {
    id: "upload-documents",
    name: "Upload Documents",
    description: "Upload new aviation documents to the system",
    category: "Documents",
  },
  {
    id: "admin-access",
    name: "Admin Access",
    description: "Access to administrative functions and settings",
    category: "Admin",
  },
];

/**
 * Service for on-boarding related operations
 */
export const useOnBoardingService = () => {
  const apiService = useApiService();

  /**
   * Get all tenants
   */
  const getTenants = useCallback(async (): Promise<Tenant[]> => {
    try {
      const options: ApiCallOptions = {
        method: 'GET',
        endpoint: '/api/tenants',
        requireAuth: true,
      };

      const response = await apiService.apiCall<Tenant[]>(options);
      return response?.data || MOCK_TENANTS;
    } catch (error) {
      // Fallback to mock data when API fails
      console.warn('API call failed, using mock data:', error);
      return MOCK_TENANTS;
    }
  }, [apiService]);

  /**
   * Get users for a specific tenant
   */
  const getTenantUsers = useCallback(async (tenantId: string): Promise<User[]> => {
    try {
      const options: ApiCallOptions = {
        method: 'GET',
        endpoint: `/api/tenants/${tenantId}/users`,
        requireAuth: true,
      };

      const response = await apiService.apiCall<User[]>(options);
      return response?.data || MOCK_USERS.filter(u => u.tenantId === tenantId);
    } catch (error) {
      // Fallback to mock data when API fails
      console.warn('API call failed, using mock data:', error);
      return MOCK_USERS.filter(u => u.tenantId === tenantId);
    }
  }, [apiService]);

  /**
   * Get available features
   */
  const getAvailableFeatures = useCallback(async (): Promise<TenantFeature[]> => {
    try {
      const options: ApiCallOptions = {
        method: 'GET',
        endpoint: '/api/features',
        requireAuth: true,
      };

      const response = await apiService.apiCall<TenantFeature[]>(options);
      return response?.data || MOCK_FEATURES;
    } catch (error) {
      // Fallback to mock data when API fails
      console.warn('API call failed, using mock data:', error);
      return MOCK_FEATURES;
    }
  }, [apiService]);

  /**
   * Update tenant feature mapping
   */
  const updateTenantFeatures = useCallback(async (
    tenantId: string,
    features: string[]
  ): Promise<void> => {
    try {
      const options: ApiCallOptions = {
        method: 'PUT',
        endpoint: `/api/tenants/${tenantId}/features`,
        payload: { features },
        requireAuth: true,
      };

      await apiService.apiCall<void>(options);
    } catch (error) {
      // Simulate update in mock data when API fails
      console.warn('API call failed, simulating update in mock data:', error);
      const tenant = MOCK_TENANTS.find(t => t.id === tenantId);
      if (tenant) {
        tenant.features = [...features];
      }
    }
  }, [apiService]);

  /**
   * Update tenant status
   */
  const updateTenantStatus = useCallback(async (
    tenantId: string,
    status: 'Active' | 'Inactive'
  ): Promise<void> => {
    try {
      const options: ApiCallOptions = {
        method: 'PUT',
        endpoint: `/api/tenants/${tenantId}/status`,
        payload: { status },
        requireAuth: true,
      };

      await apiService.apiCall<void>(options);
    } catch (error) {
      // Simulate update in mock data when API fails
      console.warn('API call failed, simulating update in mock data:', error);
      const tenant = MOCK_TENANTS.find(t => t.id === tenantId);
      if (tenant) {
        tenant.status = status;
      }
    }
  }, [apiService]);

  return {
    getTenants,
    getTenantUsers,
    getAvailableFeatures,
    updateTenantFeatures,
    updateTenantStatus,
  };
};